import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import ILeadData, {
  IDataEntryLeadCount,
  IColumn,
  IStateDistrict,
  ColumnPreference,
} from "../../models/LeadData.model";
import {
  createKanbanBoard,
  fetchAssigneeData,
  fetchAssigneeLogs,
  fetchStatesData,
  getDataEntryLeadCount,
  getLeadLogCount,
  updateKanbanBoard,
  updateSourceType,
  getLeadsByStatus,
  updateUserColumnPreferences,
  getLeads,
  deleteKanbanLeadApi,
  fetchTalukasByDistrict,
} from "../../api/kanban.api";

import { bulkUpdateAssignee, updateAssignee } from "../../api/Assignee.api";
import toast from "react-hot-toast";
import { fetchLeadStatuses } from "../../api/Lead.api";

export interface IKanbanState {
  data: Array<ILeadData>;
  columns: Record<string, IColumn>;
  loading: boolean;
  error: string | null;
  assignees: String[];
  visibleColumns: Record<string, boolean>;
  todayLeadLogCount: number;
  dataEntryLeadCount: IDataEntryLeadCount[];
  statesData: IStateDistrict[];
  talukasData: { district: string; taluka: string[] } | null;
  loadingTalukas: boolean;
  lastFilters?: {
    assignee?: string;
    sourceType?: string;
    date?: string;
    state?: string;
    district?: string;
    pincode?: string;
    searchQuery?: string;
  };
  columnLoading: Record<string, boolean>;
  columnPages: Record<string, number>;
  columnHasMore: Record<string, boolean>;
  columnOrder: IColumn[];
  userColumnPrefsLoaded: boolean;
  listLeads: ILeadData[];
  listPage: number;
  listHasMore: boolean;
  listLoading: boolean;
}

const initialState: IKanbanState = {
  data: [],
  columns: {},
  loading: false,
  error: null,
  assignees: [],
  visibleColumns: {},
  todayLeadLogCount: 0,
  dataEntryLeadCount: [],
  statesData: [],
  talukasData: null,
  loadingTalukas: false,
  lastFilters: undefined,
  columnLoading: {},
  columnPages: {},
  columnHasMore: {},
  columnOrder: [],
  userColumnPrefsLoaded: false,
  listLeads: [],
  listPage: 1,
  listHasMore: true,
  listLoading: false,
};

export interface LeadsPayload {
  [key: string]: { [key: string]: ILeadData };
}

// Fetch leads for a specific status
export const fetchLeadsByStatus = createAsyncThunk(
  "kanban/fetchLeadsByStatus",
  async ({
    status,
    page = 1,
    limit = 20,
    filters,
    append = false,
  }: {
    status: string;
    page?: number;
    limit?: number;
    filters?: any;
    append?: boolean;
  }) => {
    const result = await getLeadsByStatus(status, page, limit, filters);
    return { ...result, status, append };
  },
);

export const fetchLeads = createAsyncThunk(
  "kanban/fetchLeads",
  async ({
    page = 1,
    limit = 50,
    filters,
    append = false,
  }: {
    page?: number;
    limit?: number;
    filters?: any;
    append?: boolean;
  }) => {
    const result = await getLeads(page, limit, filters);
    return { ...result, append };
  },
);

export const fetchAllColumns = createAsyncThunk(
  "kanban/fetchAllColumns",
  async () => {
    try {
      const statusColumns = await fetchLeadStatuses("getFullResponse");
      return statusColumns;
    } catch (error) {
      console.error("Error fetching Kanban Columns:", error);
      throw error;
    }
  },
);

// Fetch initial data for all columns
export const fetchKanbanData = createAsyncThunk(
  "kanban/fetchKanbanData",
  async (
    filters: {
      assignee?: string;
      sourceType?: string;
      date?: string;
      state?: string;
      district?: string;
      city?: string;
      pincode?: string;
      searchQuery?: string;
    } = {},

    { dispatch },
  ) => {
    const statusColumns = await fetchLeadStatuses("getFullResponse");

    // Then fetch initial leads for each status
    const promises = statusColumns.map((column: IColumn) =>
      dispatch(
        fetchLeadsByStatus({
          status: column.name,
          page: 1,
          limit: 20,
          filters,
          append: false,
        }),
      ),
    );

    await Promise.all(promises);
    return { statusColumns, filters };
  },
);

export const deleteKanbanLead = createAsyncThunk(
  "kanban/removeLead",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const response = await deleteKanbanLeadApi(leadId);
      return { leadId, message: response.message };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to delete lead");
    }
  },
);

export const fetchAssignee = createAsyncThunk<any, void>(
  "kanban/getAssign",
  async (_, { rejectWithValue }) => {
    try {
      return fetchAssigneeData();
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Assignees");
    }
  },
);

export const getAssigneeLogs = createAsyncThunk(
  "kanban/assigneeLogs",
  async (leadId: string, { rejectWithValue }) => {
    try {
      return await fetchAssigneeLogs(leadId);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch assignee logs");
    }
  },
);

export const fetchLeadLogCount = createAsyncThunk(
  "kanban/fetchTodayLogCount",
  async (date: string | undefined, { rejectWithValue }) => {
    try {
      return await getLeadLogCount(date);
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch today's log count",
      );
    }
  },
);

export const fetchDataEntryLeadCount = createAsyncThunk<
  IDataEntryLeadCount[],
  { startDate: string; endDate?: string }
>(
  "leads/fetchDataEntryLogs",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      return await getDataEntryLeadCount(startDate, endDate);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to load data-entry logs");
    }
  },
);

// Add Kanban Data
export const addKanbanData = createAsyncThunk(
  "leads/add",
  async (payload: ILeadData, { rejectWithValue }) => {
    try {
      return createKanbanBoard(payload);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add data",
      );
    }
  },
);

export const updateAssigneeRole = createAsyncThunk(
  "lead/updateAssigneeRole",
  async (
    {
      leadId,
      assigneeId,
      index,
      columnIndex,
    }: {
      leadId: string;
      assigneeId: string;
      index: number;
      columnIndex: string;
    },
    { rejectWithValue }: { rejectWithValue: any },
  ) => {
    try {
      const res = await updateAssignee(leadId, assigneeId);
      return { res, index, columnIndex };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);

// Create an async action to update the card's status in the database
export const updateCardStatus = createAsyncThunk(
  "kanban/updateCardStatus",
  async (
    cardData: {
      currentStatus: string;
      destinationStatus: string;
      leadId: string;
      destinationIndex: number;
      currentIndex: number;
    },
    { rejectWithValue },
  ) => {
    const {
      currentStatus,
      destinationStatus,
      leadId,
      destinationIndex,
      currentIndex,
    } = cardData;

    try {
      return updateKanbanBoard(
        currentStatus,
        destinationStatus,
        leadId,
        destinationIndex,
        currentIndex,
      );
    } catch (error: any) {
      console.error("Error updating card status:", error);
      return rejectWithValue(error?.message || "Failed to update card status.");
    }
  },
);

export const updateSourceTypes = createAsyncThunk(
  "kanban/updateSourceType",
  async (
    { leadId, sourceType }: { leadId: string; sourceType: string },
    { dispatch },
  ) => {
    try {
      const response = await updateSourceType(leadId, sourceType);
      toast.success("Source type updated successfully!");
      return response;
    } catch (error) {
      toast.error("Error updating source type");
      throw error;
    }
  },
);

export const bulkUpdateAssignees = createAsyncThunk(
  "kanban/bulkUpdateAssignees",
  async (
    { leadIds, assigneeId }: { leadIds: string[]; assigneeId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await bulkUpdateAssignee(leadIds, assigneeId);
      // Return both leadIds and the full assignee object
      return {
        leadIds,
        assignee: {
          _id: assigneeId,
          username: response.username, // Make sure your API returns the username
        },
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to bulk update assignees",
      );
    }
  },
);

export const getStatesData = createAsyncThunk(
  "states/fetchData",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchStatesData();
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch states data");
    }
  },
);

export const getTalukasByDistrict = createAsyncThunk(
  "states/fetchTalukasByDistrict",
  async (district: string, { rejectWithValue }) => {
    try {
      return await fetchTalukasByDistrict(district);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch talukas data");
    }
  },
);

export const saveUserColumnPreferences = createAsyncThunk<
  ColumnPreference[],
  ColumnPreference[]
>(
  "kanban/saveColumnPreferences/",
  async (prefs, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateUserColumnPreferences(prefs);
      dispatch(updateColumns({ columns: {} }));
      return response;
    } catch (e: any) {
      toast.error("Failed to save column preferences");
      return rejectWithValue(e.message);
    }
  },
);

const kanbanSlice = createSlice({
  name: "kanban",
  initialState,
  reducers: {
    updateColumns(state, action) {
      const { columns } = action.payload;
      state.columns = columns;
    },
    reorderCardsInColumn(state, action) {
      const { column, dragIndex, hoverIndex } = action.payload;

      // Find the column data
      const columnData = state.columns[column]?.cards;

      if (columnData) {
        // Swap the items
        const [removed] = columnData.splice(dragIndex, 1);
        columnData.splice(hoverIndex, 0, removed);

        // Update the column's cards in the state
        state.columns[column].cards = columnData;
      }
    },

    removeColumnFromState: (state, action) => {
      state.columns = Object.fromEntries(
        Object.entries(state.columns).filter(
          ([_, col]) => col.id !== action.payload,
        ),
      );
    },

    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const columnId = action.payload;
      state.visibleColumns[columnId] = !state.visibleColumns[columnId];
    },
    setInitialVisibleColumns(
      state,
      action: PayloadAction<Record<string, boolean>>,
    ) {
      state.visibleColumns = action.payload;
    },

    resetColumnPagination(state, action: PayloadAction<string>) {
      const status = action.payload;
      state.columnPages[status] = 1;
      state.columnHasMore[status] = true;
    },
    setColumnOrder: (state, action: PayloadAction<IColumn[]>) => {
      state.columnOrder = action.payload;
    },

    updateCardField(
      state,
      action: PayloadAction<{ leadId: string; patch: Partial<ILeadData> }>,
    ) {
      const { leadId, patch } = action.payload;
      Object.values(state.columns).forEach((col) => {
        col.cards = col.cards.map((c: ILeadData) =>
          c._id === leadId ? { ...c, ...patch } : c,
        );
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllColumns.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllColumns.fulfilled, (state, action) => {
        state.loading = false;

        const data: { [key: string]: IColumn } = action.payload.reduce(
          (acc: { [key: string]: IColumn }, status: IColumn) => {
            acc[status.id] = {
              ...status,
            };
            return acc;
          },
          {},
        );
        state.columns = data;
        action.payload.map((col: IColumn) => {
          state.visibleColumns[col.id] = col.isVisible;
          state.columnPages[col?.name] = 1;
          state.columnHasMore[col?.name] = true;
          state.columnLoading[col?.name] = false;
        });
      })
      .addCase(fetchAllColumns.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updateCardStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCardStatus.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(updateCardStatus.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          (action.error as any)?.response?.data?.data ||
          "Failed to update card status";
      })
      .addCase(addKanbanData.fulfilled, (state, action) => {
        state.loading = false;

        // Destructure the actual objects
        const { savedLeadData } = action.payload;

        // Now pull `status` from savedLeadData
        const { status } = savedLeadData;

        // Find the column that matches this status
        const column = Object.values(state.columns).find(
          (col) => col.name === status,
        );

        if (column) {
          column.cards.push({
            ...savedLeadData,
            _id: savedLeadData._id || "",
            columnId: column.id,
          });
        }
      })

      .addCase(fetchAssignee.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssignee.fulfilled, (state, action) => {
        state.loading = false;
        state.assignees = action.payload;
      })
      .addCase(fetchAssignee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addKanbanData.pending, (state) => {
        state.loading = true;
      })
      .addCase(addKanbanData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to add card";
      })
      .addCase(updateAssigneeRole.fulfilled, (state, action) => {
        if (action.payload.res) {
          state.columns[action.payload.columnIndex].cards[
            action.payload.index
          ].assignee = action.payload.res;
        }
      })

      .addCase(updateSourceTypes.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      .addCase(updateSourceTypes.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateSourceTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update source type";
      })

      .addCase(bulkUpdateAssignees.fulfilled, (state, action) => {
        const { leadIds, assignee } = action.payload;

        // Update all columns with the new assignee for the affected leads
        Object.values(state.columns).forEach((column) => {
          column.cards = column.cards.map((card: ILeadData) => {
            if (leadIds.includes(card._id!)) {
              return {
                ...card,
                assignee: {
                  _id: assignee._id,
                  username: assignee.username,
                },
              };
            }
            return card;
          });
        });
      })
      .addCase(bulkUpdateAssignees.rejected, (state, action) => {
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Bulk assignee update failed";
      })
      .addCase(getAssigneeLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAssigneeLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle the fetched logs here if needed
      })
      .addCase(getAssigneeLogs.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch assignee logs";
      })
      .addCase(fetchLeadLogCount.fulfilled, (state, action) => {
        state.todayLeadLogCount = action.payload;
      })
      .addCase(fetchLeadLogCount.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(fetchDataEntryLeadCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataEntryLeadCount.fulfilled, (state, action) => {
        state.loading = false;
        state.dataEntryLeadCount = action.payload;
      })
      .addCase(fetchDataEntryLeadCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getStatesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStatesData.fulfilled, (state, action) => {
        state.loading = false;
        state.statesData = action.payload;
      })
      .addCase(getStatesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTalukasByDistrict.pending, (state) => {
        state.loadingTalukas = true;
        state.error = null;
      })
      .addCase(getTalukasByDistrict.fulfilled, (state, action) => {
        state.loadingTalukas = false;
        state.talukasData = action.payload;
      })
      .addCase(getTalukasByDistrict.rejected, (state, action) => {
        state.loadingTalukas = false;
        state.error = action.payload as string;
      })
      .addCase(saveUserColumnPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveUserColumnPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.userColumnPrefsLoaded = true;
      })
      .addCase(saveUserColumnPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to save column preferences";
      })
      .addCase(fetchKanbanData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKanbanData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFilters = action.meta.arg ?? {};
        // Initialize columns from status data
        // const columns: Record<string, IColumn> = {};
        const updatedVisibleColumns: Record<string, boolean> = {
          ...state.visibleColumns,
        };

        state.visibleColumns = updatedVisibleColumns;
      })
      .addCase(fetchKanbanData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch initial data";
      })

      // Handle fetchLeadsByStatus
      .addCase(fetchLeadsByStatus.pending, (state, action) => {
        const { status } = action.meta.arg;
        state.columnLoading[status] = true;
      })
      .addCase(fetchLeadsByStatus.fulfilled, (state, action) => {
        const { leads, status, append, page, hasMore } = action.payload;
        state.columnLoading[status] = false;
        state.columnPages[status] = page;
        state.columnHasMore[status] = hasMore;

        // Find the column by status name
        const column = Object.values(state.columns).find(
          (col) => col.name === status,
        );

        if (column) {
          const leadsWithColumnId: ILeadData[] = leads.map(
            (lead: ILeadData) => ({
              ...lead,
              columnId: column.id,
            }),
          );
          if (append) {
            // Append to existing leads
            column.cards = [...column.cards, ...leadsWithColumnId];
          } else {
            // Replace existing leads
            column.cards = leadsWithColumnId;
          }
        }
      })
      .addCase(fetchLeadsByStatus.rejected, (state, action) => {
        const { status } = action.meta.arg;
        state.columnLoading[status] = false;
        state.error = action.error.message || "Failed to fetch leads";
      })
      // Handle fetchLeads
      .addCase(fetchLeads.pending, (state, action) => {
        state.listLoading = true;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        const { leads, append, page, hasMore } = action.payload;
        state.listLoading = false;
        state.listPage = page;
        state.listHasMore = hasMore;
        state.listLeads = append ? [...state.listLeads, ...leads] : leads;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.error.message || "Failed to fetch leads";
      })
      .addCase(deleteKanbanLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKanbanLead.fulfilled, (state, action) => {
        state.loading = false;
        const { leadId } = action.payload;

        Object.values(state.columns).forEach((col) => {
          col.cards = col.cards.filter((card) => card._id !== leadId);
        });
      })
      .addCase(deleteKanbanLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  reorderCardsInColumn,
  toggleColumnVisibility,
  setInitialVisibleColumns,
  removeColumnFromState,
  resetColumnPagination,
  setColumnOrder,
  updateColumns,
  updateCardField,
} = kanbanSlice.actions;

export default kanbanSlice.reducer;
